import { useParams, Outlet, Link, useSearchParams } from "react-router-dom";
import { HouseholdLayout } from '@/components/layouts/HouseholdLayout.jsx';
import { Logo } from '@/components/Logo';
import { PageHeader } from '@/components/PageHeader';
import { useGetRecipesQuery } from '../api';
import { useGetFavoritesQuery } from '../favoritesApi';
import { Stack, Input, Box, MenuItem, Typography } from '@mui/joy';
import { ApiError } from '@/components/ApiError';
import FavoriteIcon from '@mui/icons-material/Favorite';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import { useState, useEffect, useMemo } from 'react';
import { categoriesOptions, newSort, newRecipeFilters } from '@/features/recipes/schema.js';
import { sortColumnOptions, DEFAULT_SORT_COLUMN, sortRecipes, filterRecipes, computeRecipeBounds, countActiveFilters } from '@/features/recipes/filters.js';
import { parseRangeParam, parseSortParams } from '@/utils/filters';
import { SelectableButtonBar } from '@/components/SelectableButtonBar';
import InputRoundedIcon from '@mui/icons-material/InputRounded';
import { RecipeCard } from '@/features/recipes/components/RecipeCard';
import { AddRecipeButton } from '@/features/recipes/components/AddRecipeButton';
import { SortMenu } from '@/components/SortMenu';
import { RecipeFiltersButton } from '@/features/recipes/components/RecipeFiltersDrawer';
import { RecipesSkeleton } from '@/features/recipes/components/RecipesSkeleton';
import { RecipeGrid } from '@/features/recipes/components/RecipeGrid';
import { ScrollShortcuts } from '@/features/recipes/components/ScrollShortcuts';
import { ActiveUserMenu } from '@/components/ActiveUserMenu';
import { useTheme, useMediaQuery } from '@mui/material';
import { SearchBar } from '@/ui/SearchBar';
import { PAGE_MAX_WIDTH_NARROW } from '@/theme.js';

const RANGE_FILTER_KEYS = { prepTime: 'prep', cookTime: 'cook', totalTime: 'total', calories: 'cal' };

export function RecipesPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { householdId } = useParams();
  const recipes = useGetRecipesQuery({ householdId });
  const favorites = useGetFavoritesQuery({ householdId });
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState(() => {
    const defaults = newRecipeFilters();
    const tagParam = searchParams.get('tag');
    const categoriesParam = searchParams.get('categories');
    const ingredientsParam = searchParams.get('ingredients');
    const favoritesParam = searchParams.get('favorites');
    const triedParam = searchParams.get('tried');
    return {
      ...defaults,
      categories: categoriesParam ? categoriesParam.split(',').filter(Boolean) : defaults.categories,
      tags: tagParam ? tagParam.split(',').filter(Boolean) : defaults.tags,
      ingredients: ingredientsParam ? ingredientsParam.split(',').filter(Boolean) : defaults.ingredients,
      favorites: favoritesParam === 'true',
      tried: triedParam === 'true',
      prepTime: parseRangeParam(searchParams.get(RANGE_FILTER_KEYS.prepTime)),
      cookTime: parseRangeParam(searchParams.get(RANGE_FILTER_KEYS.cookTime)),
      totalTime: parseRangeParam(searchParams.get(RANGE_FILTER_KEYS.totalTime)),
      calories: parseRangeParam(searchParams.get(RANGE_FILTER_KEYS.calories)),
    };
  });

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sort, setSort] = useState(() => parseSortParams(searchParams, sortColumnOptions, newSort()));

  const favoriteIds = useMemo(() => {
    return new Set(favorites.data?.data || []);
  }, [favorites.data]);

  const bounds = useMemo(() => computeRecipeBounds(recipes.data?.data || []), [recipes.data]);
  const resultsCount = useMemo(
    () => filterRecipes(recipes.data?.data || [], filters, favoriteIds).length,
    [recipes.data, filters, favoriteIds]
  );

  useEffect(() => {
    const params = {};
    if (filters.tags.length > 0) {
      params.tag = filters.tags.join(',');
    }
    if (filters.categories.length > 0) {
      params.categories = filters.categories.join(',');
    }
    if (filters.ingredients.length > 0) {
      params.ingredients = filters.ingredients.join(',');
    }
    if (filters.favorites) {
      params.favorites = 'true';
    }
    if (filters.tried) {
      params.tried = 'true';
    }
    if (sort.column) {
      params.sort = sort.column;
      if (sort.direction !== 'desc') {
        params.dir = sort.direction;
      }
    }
    if (filters.prepTime) {
      params[RANGE_FILTER_KEYS.prepTime] = filters.prepTime.join('-');
    }
    if (filters.cookTime) {
      params[RANGE_FILTER_KEYS.cookTime] = filters.cookTime.join('-');
    }
    if (filters.totalTime) {
      params[RANGE_FILTER_KEYS.totalTime] = filters.totalTime.join('-');
    }
    if (filters.calories) {
      params[RANGE_FILTER_KEYS.calories] = filters.calories.join('-');
    }

    const trackedKeys = ['tag', 'categories', 'ingredients', 'favorites', 'tried', 'sort', 'dir', ...Object.values(RANGE_FILTER_KEYS)];
    if (Object.keys(params).length > 0) {
      setSearchParams(params, { replace: true });
    } else if (trackedKeys.some((key) => searchParams.has(key))) {
      setSearchParams({}, { replace: true });
    }
  }, [filters.tags, filters.categories, filters.ingredients, filters.favorites, filters.tried, sort.column, sort.direction, filters.prepTime, filters.cookTime, filters.totalTime, filters.calories, setSearchParams, searchParams]);

  return (
    <HouseholdLayout backgroundColor="gray" addAction={isMobile && <AddRecipeButton type="mobile" />}>      
      <Stack direction="column" sx={{ maxWidth: PAGE_MAX_WIDTH_NARROW, margin: '0 auto', width: '100%', flex: 1 }}>
        {isMobile && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: '16px' }}>
            <Logo />
            <ActiveUserMenu />
          </Stack>
        )}
        <Box sx={{ mb: { xs: '12px', md: '16px' } }}>
        <PageHeader title="Your recipes" suffix={`${resultsCount} saved`} hideOnMobile>
          <SearchBar value={filters.search} onChange={(value) => setFilters({ ...filters, search: value })} entity="recipes" sx={{ width: { xs: '100%', md: 'auto' }, minWidth: { md: '320px' } }} />
          {!isMobile && <AddRecipeButton type="desktop" />}
        </PageHeader>
        </Box>
        <RecipeFilters
          filters={filters}
          setFilters={setFilters}
          sort={sort}
          setSort={setSort}
          bounds={bounds}
          resultsCount={resultsCount}
          filtersOpen={filtersOpen}
          setFiltersOpen={setFiltersOpen}
        />
        <Recipes recipes={recipes} filters={filters} sort={sort} favoriteIds={favoriteIds} />
      </Stack>
      {isMobile && <ScrollShortcuts onOpenFilters={() => setFiltersOpen(true)} activeFilterCount={countActiveFilters(filters)} />}
      <Outlet />
    </HouseholdLayout>
  );
}

function MoreActions() {
  return (
    <Link to={`./imports`}><MenuItem><InputRoundedIcon/> Imports</MenuItem></Link>
  );
}

function RecipeFilters({ filters, setFilters, sort, setSort, bounds, resultsCount, filtersOpen, setFiltersOpen }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isCategorySelected = (category) => filters.categories.includes(category);

  const toggleCategory = (category) => {
    const categories = isCategorySelected(category)
      ? filters.categories.filter((c) => c !== category)
      : [...filters.categories, category];
    setFilters({ ...filters, categories });
  };

  const options = [
    {
      key: 'all',
      label: 'All',
      selected: !filters.favorites && !filters.tried && filters.categories.length === 0,
      onClick: () => setFilters({ ...filters, categories: [], favorites: false, tried: false }),
    },
    {
      key: 'favorites',
      label: <><FavoriteIcon sx={{ fontSize: 16, mr: 0.5 }} /> Favorites</>,
      selected: filters.favorites,
      onClick: () => setFilters({ ...filters, favorites: !filters.favorites }),
    },
    {
      key: 'tried',
      label: <><RestaurantOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} /> Tried</>,
      selected: filters.tried,
      onClick: () => setFilters({ ...filters, tried: !filters.tried }),
    },
    ...categoriesOptions.map((category) => ({
      key: category.value,
      label: category.label,
      selected: isCategorySelected(category.value),
      onClick: () => toggleCategory(category.value),
    })),
  ];

  return (
    <Stack sx={{ mb: '14px', justifyContent: 'space-between', alignItems: 'stretch' }} gap={{'xs': '18px', 'md': '0'}} direction={{'xs': 'column', 'md': 'row'}}>
      <SelectableButtonBar options={options} fadeColor="var(--joy-palette-neutral-100)" />
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ width: { xs: '100%', md: 'auto' } }}>
        {isMobile && <Typography level="title-sm">
          {resultsCount} {resultsCount === 1 ? 'Recipe' : 'Recipes'}
        </Typography>}
        <Stack direction="row" gap={1}>          
          <SortMenu
            sort={sort}
            setSort={setSort}
            columnOptions={sortColumnOptions}
            defaultColumn={DEFAULT_SORT_COLUMN}
          />
          <RecipeFiltersButton
            filters={filters}
            setFilters={setFilters}
            bounds={bounds}
            resultsCount={resultsCount}
            open={filtersOpen}
            onOpenChange={setFiltersOpen}
            sx={{ width: 'auto' }}
          />
        </Stack>
      </Stack>
    </Stack>
  );
}

function Recipes({ recipes, filters, sort, favoriteIds }) {
  if (recipes.isLoading) {
    return <RecipesSkeleton />;
  }

  if (recipes.error) {
    return <ApiError error={recipes.error} />;
  }

  const filteredRecipes = filterRecipes(recipes.data.data, filters, favoriteIds);
  const sortedRecipes = sortRecipes(filteredRecipes, sort);

  return (
    <RecipeGrid recipes={sortedRecipes} favoriteIds={favoriteIds} card={
      (recipe, isMobile) => <RecipeCard recipe={recipe} isFavorite={favoriteIds.has(recipe.id)} isMobile={isMobile} />
    } />
  );
}
